import { useState } from "react";
import * as Icons from "lucide-react";

const Ico = ({ name, className }) => { const C = Icons[name] || Icons.Dot; return <C className={className} />; };

const SEV_DOT = { critical: "bg-red-500", high: "bg-orange-500", medium: "bg-amber-500", low: "bg-slate-300" };

// True if the card carries an Allison flag matching the given Watchtower row key
// (missing_form is an aggregate of every missing_form_* flag).
export function cardHasFlag(card, watchKey) {
  const flags = card.allison_flags || [];
  if (watchKey === "missing_form") return flags.some((f) => f.key.startsWith("missing_form"));
  return flags.some((f) => f.key === watchKey);
}

function Group({ title, icon, tone, rows, empty, testid, onRowClick }) {
  const toneCls = { red: "text-red-600", amber: "text-amber-600", green: "text-emerald-600" }[tone];
  const clickable = !!onRowClick && tone !== "green";
  return (
    <div className="flex-1 min-w-[200px]" data-testid={testid}>
      <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide mb-1.5 ${toneCls}`}>
        <Ico name={icon} className="h-3.5 w-3.5" />{title}
      </div>
      {rows.length === 0 ? (
        <div className="text-[11px] text-slate-400">{empty}</div>
      ) : (
        <div className="space-y-1">
          {rows.map((r) => {
            const inner = (
              <>
                {tone !== "green" && <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${SEV_DOT[r.severity] || SEV_DOT.low}`} />}
                {tone === "green" && <Ico name={r.icon || "ArrowRight"} className="h-3 w-3 text-emerald-500 shrink-0" />}
                <span className="font-semibold tabular-nums">{r.count != null ? r.count : ""}</span>
                <span className="truncate">{r.label.replace(/^\d+\s/, "")}</span>
                {clickable && <Ico name="ChevronRight" className="h-3 w-3 text-slate-300 shrink-0 ml-auto" />}
              </>
            );
            return clickable ? (
              <button key={r.key} onClick={() => onRowClick(r)} data-testid={`wt-row-${r.key}`}
                className="w-full flex items-center gap-2 text-[12px] text-slate-700 rounded-md px-1.5 py-1 -mx-1.5 hover:bg-slate-100 transition-colors text-left group">
                {inner}
              </button>
            ) : (
              <div key={r.key} className="flex items-center gap-2 text-[12px] text-slate-700 px-1.5 py-1" data-testid={`wt-row-${r.key}`}>{inner}</div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Watchtower({ data, onRowClick }) {
  const [open, setOpen] = useState(true);
  if (!data) return null;
  const { critical = [], attention = [], recommendations = [], health_score = 100, avg_days_in_stage = 0, flagged_cards = 0, critical_cards = 0 } = data;
  const healthTone = health_score >= 85 ? "text-emerald-600" : health_score >= 65 ? "text-amber-600" : "text-red-600";
  const healthBar = health_score >= 85 ? "bg-emerald-500" : health_score >= 65 ? "bg-amber-500" : "bg-red-500";

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="allison-watchtower">
      <button onClick={() => setOpen(!open)} className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-slate-50 transition-colors" data-testid="watchtower-toggle">
        <span className="flex items-center gap-2">
          <span className="h-7 w-7 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center"><Icons.Sparkles className="h-4 w-4" /></span>
          <span className="text-sm font-bold text-slate-800">Allison Watchtower</span>
          {critical_cards > 0 && <span className="text-[10px] font-semibold text-red-700 bg-red-100 rounded-full px-2 py-0.5" data-testid="wt-critical-count">{critical_cards} critical</span>}
          <span className="text-[10px] font-medium text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{flagged_cards} flagged</span>
        </span>
        <span className="flex items-center gap-3">
          <span className="hidden sm:flex items-center gap-1.5 text-[11px] text-slate-400"><Icons.Timer className="h-3.5 w-3.5" />Avg {avg_days_in_stage}d in stage</span>
          <span className="flex items-center gap-1.5">
            <span className={`text-sm font-bold ${healthTone}`} data-testid="wt-health">{health_score}%</span>
            <span className="text-[10px] text-slate-400">health</span>
          </span>
          <Icons.ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && (
        <div className="px-4 pb-3 pt-1 border-t border-slate-100">
          <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden mb-3">
            <div className={`h-full rounded-full transition-all ${healthBar}`} style={{ width: `${health_score}%` }} />
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            <Group title="Critical" icon="Siren" tone="red" rows={critical} empty="No critical breaches 🎉" testid="wt-critical" onRowClick={onRowClick} />
            <Group title="Needs Attention" icon="AlertTriangle" tone="amber" rows={attention} empty="Nothing needs attention" testid="wt-attention" onRowClick={onRowClick} />
            <Group title="Recommendations" icon="Lightbulb" tone="green" rows={recommendations} empty="No recommendations right now" testid="wt-recommendations" />
          </div>
        </div>
      )}
    </div>
  );
}

// Merge multiple board watchtowers into one (used by the Lifecycle super-board).
export function mergeWatchtowers(list) {
  const towers = list.filter(Boolean);
  if (!towers.length) return null;
  const mergeRows = (key) => {
    const map = {};
    towers.forEach((t) => (t[key] || []).forEach((r) => {
      const e = map[r.key] || { ...r, count: 0 };
      e.count = (e.count || 0) + (r.count || 0);
      if (r.severity && (!e.severity || ({ critical: 3, high: 2, medium: 1, low: 0 }[r.severity] > { critical: 3, high: 2, medium: 1, low: 0 }[e.severity]))) e.severity = r.severity;
      map[r.key] = e;
    }));
    return Object.values(map).filter((r) => r.count > 0);
  };
  const total = towers.reduce((a, t) => a + (t.total_cards || 0), 0) || 1;
  return {
    critical: mergeRows("critical"),
    attention: mergeRows("attention"),
    recommendations: mergeRows("recommendations"),
    flagged_cards: towers.reduce((a, t) => a + (t.flagged_cards || 0), 0),
    critical_cards: towers.reduce((a, t) => a + (t.critical_cards || 0), 0),
    total_cards: towers.reduce((a, t) => a + (t.total_cards || 0), 0),
    health_score: (() => { const v = Math.round(towers.reduce((a, t) => a + (t.health_score ?? 100) * (t.total_cards || 0), 0) / total); return Number.isFinite(v) ? v : 100; })(),
    avg_days_in_stage: (() => { const v = Math.round((towers.reduce((a, t) => a + (t.avg_days_in_stage || 0) * (t.total_cards || 0), 0) / total) * 10) / 10; return Number.isFinite(v) ? v : 0; })(),
  };
}
